import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRightIcon,
  CalendarClockIcon,
  GavelIcon,
  ScaleIcon,
  TriangleAlertIcon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

import type { PreviewFile } from "@/components/files/file-preview-dialog";
import { FilePreviewDialog } from "@/components/files/file-preview-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { ProcedureLegalRegistry, ProcedureRow } from "../api/use-procedures";
import { useProcedureLegalRegistry } from "../api/use-procedures";
import { DECISION_DOCUMENT_TYPES, OUTCOME_ALERT_VARIANT } from "../lib/decision-outcome";
import { formatLongDate, formatRelativeDate, toDate } from "../lib/procedure-format";
import { PANEL_FADE } from "../lib/rail-motion";
import { DocumentChip, LegalRegistryTimeline } from "./legal-registry-timeline";
import { BadgeProcedurePosition, BadgeRenvois } from "./procedure-badges";

/**
 * Small caps get *positive* tracking (they read cramped otherwise) — the mirror
 * of the negative tracking on the case number above.
 */
const SECTION_LABEL = "text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-gray-500";

/**
 * Sections are separated by a real rule, not just whitespace: the panel stacks
 * four unrelated kinds of content and needs visible seams to stay scannable.
 */
function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="mt-6 border-t border-gray-200 pt-5 first:mt-0 first:border-t-0 first:pt-0">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className={SECTION_LABEL}>{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs leading-5 text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium leading-5 tabular-nums text-gray-900">{value}</dd>
    </div>
  );
}

// `role` would read as the ARIA attribute here — the party's side is data, not a11y.
function PartyCard({ side, party }: { side: string; party: ProcedureLegalRegistry["demandeurs"][number] }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className={cn(SECTION_LABEL, "mb-1 text-[0.625rem]")}>{side}</p>
      <p className="text-sm font-medium leading-5 text-gray-900">{party.nomOuDenomination}</p>
      <div className="mt-1 space-y-0.5">
        {party.numeroSiren ? (
          <p className="text-xs leading-5 tabular-nums text-gray-500">SIREN {party.numeroSiren}</p>
        ) : null}
        {party.adresse ? <p className="text-xs leading-5 text-gray-500">{party.adresse}</p> : null}
        {party.representants.length > 0 ? (
          <p className="text-xs leading-5 text-gray-500">
            Représenté par <span className="text-gray-700">{party.representants.join(", ")}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The one fact a lawyer opens a case file for: what is the next deadline. A
 * closed case has none, so it shows the decision instead; a live case with
 * nothing scheduled says so explicitly, because "no hearing booked" is itself
 * actionable information.
 */
function Deadline({
  detail,
  onPreview,
}: {
  detail: ProcedureLegalRegistry;
  onPreview: (file: PreviewFile) => void;
}) {
  const hearingDate = toDate(detail.prochaineAudienceDate);

  if (detail.terminee) {
    // On a closed case the judgment is the document you actually want; surface
    // it here instead of making people hunt down the timeline.
    const decisionDocument = detail.affaireHistorique
      .map((entry) => entry.document)
      .find((document) => document && DECISION_DOCUMENT_TYPES.includes(document.typeName));
    // Colour follows the outcome, not the mere fact of closing: « Rejet de la
    // demande » is a loss and must never be painted green.
    const outcome = detail.decisionOutcome;
    const Icon = outcome === "lost" ? XCircleIcon : GavelIcon;

    return (
      <Alert variant={OUTCOME_ALERT_VARIANT[outcome]}>
        <Icon aria-hidden="true" />
        <AlertTitle>Affaire terminée</AlertTitle>
        <AlertDescription>
          <span className="font-medium text-foreground">{detail.decision ?? "Décision rendue"}</span>
          {detail.dateDecision ? (
            <span className="tabular-nums">{formatLongDate(detail.dateDecision)}</span>
          ) : null}
          {decisionDocument ? (
            <span>
              <DocumentChip document={decisionDocument} onPreview={onPreview} />
            </span>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  }

  if (!hearingDate) {
    return (
      <Alert variant="warning">
        <TriangleAlertIcon aria-hidden="true" />
        <AlertTitle>Prochaine audience</AlertTitle>
        <AlertDescription>Aucune date dans Tribunal Digital.</AlertDescription>
      </Alert>
    );
  }

  const detailLine = [
    detail.prochaineAudienceHeure,
    detail.prochaineAudienceChambre ? `chambre ${detail.prochaineAudienceChambre}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Alert variant="info">
      <CalendarClockIcon aria-hidden="true" />
      <AlertTitle>Prochaine audience</AlertTitle>
      <AlertDescription>
        <span className="text-base font-semibold tracking-[-0.01em] text-foreground">
          {formatLongDate(hearingDate)}
        </span>
        {detailLine ? <span>{detailLine}</span> : null}
        {/* How many times this has already slipped is the question that comes
            right after the date — so it sits with it, not in the facts grid. */}
        <span>
          <BadgeRenvois count={detail.nbRenvois} ultime={detail.ultimeRenvoi} />
        </span>
      </AlertDescription>
    </Alert>
  );
}

function AffaireBody({ detail }: { detail: ProcedureLegalRegistry }) {
  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);
  const documentCount = detail.affaireHistorique.filter((entry) => entry.document !== null).length;

  return (
    <>
      <FilePreviewDialog
        open={previewedFile !== null}
        file={previewedFile}
        onClose={() => setPreviewedFile(null)}
      />

      <Deadline detail={detail} onPreview={setPreviewedFile} />

      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        {detail.typeLabel ? (
          <span className="inline-flex items-center rounded-md bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-600/20">
            {detail.typeLabel}
          </span>
        ) : null}
        {/* No « Terminée » badge here: the banner above already states the
            outcome, and repeating it competes with the position badge. */}
        {/* The « ultime » flag rides along with the renvoi count in the banner. */}
        {!detail.terminee && detail.position ? <BadgeProcedurePosition position={detail.position} /> : null}
      </div>

      {/* `natureDemande` is dropped on purpose: it repeats the procedure type in
          registry boilerplate ("Requête en règlement d'une facture (procédure
          Europ. …)") and never varies within a wave. */}
      <dl className="mt-5 grid grid-cols-2 gap-x-3 rounded-lg bg-gray-50 p-3">
        <Fact label="Enrôlement" value={formatLongDate(detail.dateSaisine)} />
        <Fact label="Dossier" value={detail.claim.claimNumber ? `#${detail.claim.claimNumber}` : "—"} />
      </dl>

      {detail.qualificationDecision ? (
        <div className="mt-3">
          <dt className="text-xs leading-5 text-gray-500">Qualification de la décision</dt>
          <dd className="mt-0.5 text-sm font-medium leading-5 text-gray-900">
            {detail.qualificationDecision}
          </dd>
        </div>
      ) : null}

      {/* Before the parties on purpose: the history is what a lawyer reads to
          know where the case stands; the parties rarely change. */}
      <Section
        title="Historique de l'affaire"
        aside={
          documentCount > 0 ? (
            <span className="text-xs text-gray-400">
              {documentCount} document{documentCount > 1 ? "s" : ""}
            </span>
          ) : null
        }
      >
        <LegalRegistryTimeline entries={detail.affaireHistorique} onPreview={setPreviewedFile} />
      </Section>

      <Section title="Parties">
        <div className="space-y-2">
          {detail.demandeurs.map((party) => (
            <PartyCard key={`dem-${party.nomOuDenomination}`} side="Demandeur" party={party} />
          ))}
          {detail.defendeurs.map((party) => (
            <PartyCard key={`def-${party.nomOuDenomination}`} side="Défendeur" party={party} />
          ))}
          {detail.demandeurs.length === 0 && detail.defendeurs.length === 0 ? (
            <p className="text-xs text-gray-500">Aucune partie enregistrée.</p>
          ) : null}
        </div>
      </Section>

      <p className="mt-8 border-t border-gray-100 pt-3 text-xs leading-5 text-gray-400">
        Registre synchronisé {formatRelativeDate(detail.syncedAt)}.
      </p>
    </>
  );
}

function PanelMessage({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center">
      <ScaleIcon className="mx-auto h-5 w-5 text-gray-300" aria-hidden="true" />
      <p className="mt-2 text-sm font-medium text-gray-900">{title}</p>
      <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p>
    </div>
  );
}

interface AffairePanelProps {
  procedureId: string;
  /** Row backing that id when it is on the current page — fills the header instantly. */
  hint: ProcedureRow | null;
  onClose: () => void;
}

/**
 * Selected case, shown in the rail in place of the calendar. A side panel rather
 * than a modal sheet: the table stays readable and clickable, so moving from one
 * affaire to the next never costs a dismiss.
 */
export function AffairePanel({ procedureId, hint, onClose }: AffairePanelProps) {
  const { data, isPending, isError } = useProcedureLegalRegistry(procedureId);

  // The header reads from whichever source is available first: the clicked row,
  // else the fetched detail — so a link opened cold still titles itself.
  const numeroRG = hint?.identifier ?? data?.numeroRG ?? null;
  const courtName = hint?.court.name ?? data?.court.name ?? null;
  const claimNumber = hint?.claim.claimNumber ?? data?.claim.claimNumber ?? null;
  const claimId = hint?.claim.id ?? data?.claim.id ?? null;

  return (
    // Capped to the sticky viewport slot: the panel scrolls inside itself so the
    // header and the « Ouvrir le dossier » action stay put while the table moves.
    <div className="flex max-h-[calc(100vh-4rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
      <header className="flex items-start gap-3 border-b border-gray-100 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <p className={SECTION_LABEL}>Affaire</p>
          <h2 className="mt-1 truncate text-xl font-semibold leading-tight tracking-[-0.02em] tabular-nums text-gray-900">
            {numeroRG ?? "Sans numéro RG"}
          </h2>
          <p className="mt-1 truncate text-xs leading-5 text-gray-500">
            {courtName ?? "—"}
            {claimNumber ? ` · dossier ${claimNumber}` : ""}
          </p>
        </div>
        <button
          type="button"
          aria-label="Fermer l'affaire"
          onClick={onClose}
          className="-me-1 -mt-1 rounded-md p-1.5 text-gray-400 transition-[transform,background-color] duration-100 ease-out hover:bg-gray-100 hover:text-gray-600 active:scale-95 motion-reduce:active:scale-100"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {/* The body used to teleport from skeleton to content on every row click.
            A pure cross-fade — no travel, so it needs no reduced-motion branch —
            with the ease-out curve reserved for things entering and leaving. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isPending ? "loading" : "loaded"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={PANEL_FADE}
          >
            {isPending ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full rounded-xl" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : null}

            {isError ? (
              <PanelMessage
                title="Impossible de charger l'affaire"
                description="Le détail Tribunal Digital n'a pas pu être récupéré. Réessayez dans un instant."
              />
            ) : null}

            {!isPending && !isError && !data ? (
              <PanelMessage
                title="Aucune donnée Tribunal Digital"
                description="Ce dossier n'a jamais été synchronisé avec Tribunal Digital."
              />
            ) : null}

            {data ? <AffaireBody detail={data} /> : null}
          </motion.div>
        </AnimatePresence>
      </div>

      <footer className="border-t border-gray-100 p-3">
        {/* A real link, not a navigate(): opening a claim shouldn't cost you the
            affaire you had open, and cmd/middle-click work on their own. */}
        <Button
          variant="outline"
          className="w-full"
          disabled={claimId === null}
          render={
            claimId ? (
              <Link
                to="/claims"
                search={{ claimId, claimTab: "overview" }}
                target="_blank"
                rel="noreferrer"
              />
            ) : undefined
          }
        >
          Ouvrir le dossier
          <ArrowUpRightIcon className="h-4 w-4" />
        </Button>
      </footer>
    </div>
  );
}
